import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "field-title" }

import { computed, ref, useTemplateRef, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { showError } from '@services/utils/dialog';

import NcButton from '@nextcloud/vue/components/NcButton';
import NcTextField from '@nextcloud/vue/components/NcTextField';

import Modal from './Modal.vue';
import AlbumCollaborators from './AlbumCollaborators.vue';
import XLoadingIcon from '@components/XLoadingIcon.vue';

import { useModal } from '@services/modal';
import { t } from '@services/l10n';
import { useRouteIs } from '@services/route-is';
import { isAbortError, useAbort } from '@services/utils/abort-vue';
import * as utils from '@services/utils/common';
import * as dav from '@services/dav';


export default /*@__PURE__*/_defineComponent({
  ...{
  name: 'AlbumShareModal',
},
  __name: 'AlbumShareModal',
  setup(__props) {



const route = useRoute();
const router = useRouter();
const routeIs = useRouteIs();
const modal = useTemplateRef('modal');
const { show, close } = useModal(modal);
const collaborators = useTemplateRef<InstanceType<typeof AlbumCollaborators>>('collaborators');

const album = ref<any>(null);
const albumName = ref(String());
const loadingAddCollaborators = ref(false);
const abort = useAbort();

const showEditFields = computed(() => album.value?.basename?.startsWith('.link-'));

console.assert(!_m.modals.albumShare, 'AlbumShareModal created twice');
_m.modals.albumShare = open;

async function open(user: string, name: string, link?: boolean) {
  const signal = abort.renew();
  show.value = true;

  // Load album info
  try {
    loadingAddCollaborators.value = true;
    albumName.value = name;
    album.value = await dav.getAlbum(user, name, { signal });
    signal.throwIfAborted();
  } catch (e) {
    if (isAbortError(e)) return;
    showError(t('memories', 'Failed to load album info: {name}', { name }));
    show.value = false;
    return;
  } finally {
    if (!signal.aborted) loadingAddCollaborators.value = false;
  }

  // Check if we immediately want to share a link
  if (link) {
    await nextTick(); // load collaborators component
    collaborators.value?.createPublicLinkForAlbum();
  }
}

function cleanup() {
  abort.abort();
  show.value = false;
  album.value = null;
  albumName.value = String();
  loadingAddCollaborators.value = false;
}

async function save(collaboratorsIn: any[]) {
  const signal = abort.renew();
  try {
    loadingAddCollaborators.value = true;

    // Update album collaborators
    await dav.updateAlbum(
      album.value,
      {
        albumName: album.value.basename,
        properties: { collaborators: collaboratorsIn },
      },
      { signal },
    );
    signal.throwIfAborted();

    // Update album name if changed
    if (album.value.basename !== albumName.value) {
      await dav.renameAlbum(album.value, album.value.basename, albumName.value, { signal });
      signal.throwIfAborted();

      // Change route to new album name if we're on album page
      if (routeIs.Albums) {
        // Do not await but proceed to close modal instantly
        router.replace({
          name: route.name!,
          params: {
            user: route.params.user?.toString(),
            name: albumName.value,
          },
        });
      }
    }

    // Refresh timeline for metadata changes
    utils.bus.emit('memories:timeline:soft-refresh', null);

    // Close modal
    await close();
  } catch (error) {
    if (isAbortError(error)) return;
    console.error(error);
    showError(t('memories', 'Failed to update album.'));
  } finally {
    if (!signal.aborted) loadingAddCollaborators.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  return (_unref(show))
    ? (_openBlock(), _createBlock(Modal, {
        key: 0,
        ref_key: "modal",
        ref: modal,
        onClose: cleanup
      }, {
        title: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('memories', 'Share Album')), 1)
        ]),
        default: _withCtx(() => [
          (showEditFields.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createElementVNode("span", _hoisted_1, _toDisplayString(_unref(t)('memories', 'Name of the album')), 1),
                _createVNode(_unref(NcTextField), {
                  modelValue: albumName.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((albumName).value = $event)),
                  type: "text",
                  name: "name",
                  required: true,
                  autofocus: "true",
                  placeholder: _unref(t)('memories', 'Name of the album')
                }, null, 8, ["modelValue", "placeholder"])
              ], 64))
            : _createCommentVNode("", true),
          (album.value)
            ? (_openBlock(), _createBlock(AlbumCollaborators, {
                key: 1,
                ref_key: "collaborators",
                ref: collaborators,
                "album-name": album.value.basename,
                collaborators: album.value.collaborators,
                "public-link": album.value.publicLink,
                "allow-public-link": true
              }, {
                default: _withCtx(({ collaborators }) => [
                  _createVNode(_unref(NcButton), {
                    "aria-label": _unref(t)('memories', 'Save collaborators for this album.'),
                    variant: "primary",
                    disabled: loadingAddCollaborators.value,
                    onClick: ($event: any) => (save(collaborators))
                  }, {
                    icon: _withCtx(() => [
                      (loadingAddCollaborators.value)
                        ? (_openBlock(), _createBlock(XLoadingIcon, { key: 0 }))
                        : _createCommentVNode("", true)
                    ]),
                    default: _withCtx(() => [
                      _createTextVNode(" " + _toDisplayString(_unref(t)('memories', 'Save')), 1)
                    ]),
                    _: 1
                  }, 8, ["aria-label", "disabled", "onClick"])
                ]),
                _: 1
              }, 8, ["album-name", "collaborators", "public-link"]))
            : (_openBlock(), _createBlock(XLoadingIcon, {
                key: 2,
                class: "album-share fill-block"
              }))
        ]),
        _: 1
      }, 512))
    : _createCommentVNode("", true)
}
}

})